/**
 * VÉRAULT Travels — /plan · "Begin Your Experience"
 *
 * The booking flow is scheduler-first: the embedded Nylas scheduler renders
 * immediately so the guest can pick a moment that suits them.  No inquiry
 * form upstream, no premature thank-you state.
 *
 * Once Nylas dispatches `nylas-booking-confirmed`, we:
 *   1. Persist a lead in the VT ecosystem (POST /api/inquiries with
 *      status="consultation_scheduled" + booking metadata) so the advisor
 *      sees it in their portal.
 *   2. Swap to a calm, confident confirmation pane with concierge-led copy.
 *
 * Nylas already creates the calendar event and dispatches advisor + guest
 * email confirmations on its end (it owns the scheduling configuration).
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';
import NylasScheduler from '../../components/public/NylasScheduler';
import { useContentOverrides } from '../../lib/useContentOverrides';

const API = process.env.REACT_APP_BACKEND_URL;
const NYLAS_CONFIGURATION_ID =
  process.env.REACT_APP_NYLAS_CONFIGURATION_ID || 'aafea4b5-1887-4198-899f-dce1d9edcffd';
const NYLAS_CLIENT_ID =
  process.env.REACT_APP_NYLAS_CLIENT_ID || 'ae93a2f7-6506-480c-916a-bd3b914ff84a';
const NYLAS_SCHEDULER_SLUG =
  process.env.REACT_APP_NYLAS_SCHEDULER_SLUG || 'verault-travel-45min';
const NYLAS_FALLBACK_URL = `https://book.nylas.com/us/${NYLAS_CLIENT_ID}/${NYLAS_SCHEDULER_SLUG}`;

// ---------- helpers ----------------------------------------------------

/**
 * Normalize Nylas booking event payload into the fields our /api/inquiries
 * endpoint accepts. Different versions of @nylas/web-elements emit slightly
 * different shapes; we read defensively.
 */
function shapeBooking(detail) {
  const d = detail || {};
  // Common payload paths across versions: {bookedEventInfo, booking, data, ...}
  const booking = d.booking || d.bookedEventInfo || d.data || d;
  const participant =
    booking?.primary_participant ||
    booking?.primaryParticipant ||
    booking?.participants?.[0] ||
    d?.bookingInfo?.primaryParticipant ||
    {};
  const additional =
    booking?.additional_fields ||
    booking?.additionalFields ||
    d?.bookingInfo?.additionalFields ||
    {};
  const startUnix =
    booking?.start_time ||
    booking?.startTime ||
    booking?.timeslot?.start_time ||
    booking?.timeslot?.startTime;
  const endUnix =
    booking?.end_time ||
    booking?.endTime ||
    booking?.timeslot?.end_time ||
    booking?.timeslot?.endTime;
  const toIso = (v) => {
    if (!v) return null;
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return new Date((v < 1e12 ? v * 1000 : v)).toISOString();
    if (v instanceof Date) return v.toISOString();
    return null;
  };
  return {
    name: participant?.name || d?.guestName || '',
    email: participant?.email || d?.guestEmail || '',
    phone: additional?.phone_number?.value || additional?.phone?.value || null,
    consultation_scheduled_at: toIso(startUnix),
    consultation_end_at: toIso(endUnix),
    consultation_timezone: booking?.timezone || booking?.time_zone || null,
    nylas_booking_id: booking?.booking_id || booking?.id || null,
    nylas_event_id: booking?.event_id || booking?.calendar_event_id || null,
    notes: additional?.notes?.value || additional?.message?.value || null,
    status: 'consultation_scheduled',
  };
}

// ---------- view -------------------------------------------------------

export default function PlanPage() {
  const c = useContentOverrides('plan');
  const [confirmed, setConfirmed] = useState(false);
  const [bookingDetails, setBookingDetails] = useState(null);

  // If Nylas redirected back here after a booking (the confirmation-redirect
  // attribute we pass below sends the user to ?booking=confirmed), flip to
  // the branded confirmation pane immediately. Backing this up, the
  // server-side Nylas webhook persists the lead independently — even if
  // the in-page `bookingConfirmed` event never fires.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('booking') === 'confirmed') {
      // Pull anything Nylas appended — they typically include booking_id,
      // start_time, etc. as query params.
      const detail = {
        name: params.get('name') || '',
        email: params.get('email') || '',
        consultation_scheduled_at: params.get('start_time') || null,
        consultation_end_at: params.get('end_time') || null,
        nylas_booking_id: params.get('booking_id') || null,
        status: 'consultation_scheduled',
      };
      setBookingDetails(detail);
      setConfirmed(true);
    }
  }, []);

  const handleBooking = useCallback(async (detail) => {
    const payload = shapeBooking(detail);
    setBookingDetails(payload);
    setConfirmed(true);
    if (payload.email && payload.name) {
      try {
        await axios.post(`${API}/api/inquiries`, payload);
      } catch (_) {
        // swallow — UX is unchanged either way
      }
    }
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  const bookingConfirmationRedirectUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/plan?booking=confirmed`
      : null;

  // ---------- confirmed state ----------------------------------------
  if (confirmed) {
    const when = bookingDetails?.consultation_scheduled_at;
    let prettyWhen = '';
    try {
      if (when) {
        const dt = new Date(when);
        prettyWhen = dt.toLocaleString(undefined, {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short',
        });
      }
    } catch (_) {
      prettyWhen = '';
    }
    return (
      <div data-testid="page-plan">
        {c.previewMode && (
          <div className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono" data-testid="plan-preview-banner">
            Studio draft preview &mdash; not yet published
          </div>
        )}
        <section className="py-24 sm:py-28 px-6 sm:px-8" data-testid="plan-confirmed">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-8">
              {c.get('confirmed.kicker', 'Confirmed')}
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] tracking-[-0.01em] leading-[1.1] mb-10">
              {c.get('confirmed.title', 'Your consultation has been scheduled.')}
            </h1>
            <p className="font-accent text-base sm:text-lg italic text-[#6B6B6B] leading-relaxed max-w-xl mx-auto mb-10">
              {c.get('confirmed.message', 'We look forward to learning more about your vision \u2014 and beginning to design your experience.')}
            </p>
            {prettyWhen && (
              <div className="inline-block px-7 py-4 mb-12 border border-[#E8E4DC] rounded" data-testid="plan-confirmed-time">
                <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-2">When</p>
                <p className="font-serif text-base sm:text-lg text-[#1A1A1A]">
                  {prettyWhen}
                </p>
              </div>
            )}
            <p className="text-sm text-[#999] leading-relaxed max-w-md mx-auto mb-12">
              {c.get('confirmed.fine_print', 'A calendar invitation has been sent to your inbox. In the moments before our conversation, simply consider what you would like a journey to feel like.')}
            </p>
            <div className="pt-10 border-t border-[#E8E4DC] flex flex-col sm:flex-row sm:justify-center gap-x-10 gap-y-4 text-sm">
              <a
                href="/"
                className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors underline underline-offset-4"
                data-testid="plan-confirmed-home"
              >
                Return to home
              </a>
              <a
                href="/about"
                className="text-[#6B6B6B] hover:text-[#1A1A1A] transition-colors underline underline-offset-4"
                data-testid="plan-confirmed-philosophy"
              >
                Read the philosophy
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  // ---------- scheduler-first state ----------------------------------
  return (
    <div data-testid="page-plan">
      {c.previewMode && (
        <div className="bg-[#1A1A1A] text-white text-center py-2 px-4 text-[11px] tracking-[0.22em] uppercase font-mono" data-testid="plan-preview-banner">
          Studio draft preview &mdash; not yet published
        </div>
      )}
      {/* Hero — quiet, deliberate, immediately followed by the calendar. */}
      <section className="pt-20 pb-10 sm:pt-32 sm:pb-16 px-6 sm:px-8 text-center" data-testid="plan-hero">
        <p className="text-[10px] uppercase tracking-[0.28em] text-[#C9A961] mb-8">{c.get('hero.kicker', 'The Invitation')}</p>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] tracking-[-0.01em] leading-[1.1] max-w-3xl mx-auto">
          {c.get('hero.title', 'Begin Your Experience')}
        </h1>
        <div className="w-12 h-px bg-[#E8E4DC] mx-auto my-6 sm:my-8" aria-hidden="true" />
        <p className="font-accent italic text-base sm:text-lg text-[#6B6B6B] max-w-xl mx-auto leading-relaxed">
          {c.get('hero.subtitle', 'Choose a moment that suits you. Your advisor will use this conversation to understand your vision and begin designing your experience.')}
        </p>
        <p className="mt-6 text-[10px] uppercase tracking-[0.28em] text-[#C9A961]">
          {c.get('hero.duration', '45-minute complimentary consultation')}
        </p>
      </section>

      {/* Embedded scheduler — Nylas Web Component, never an iframe. */}
      <section className="bg-white border-t border-[#E8E4DC] py-10 sm:py-20 px-6 sm:px-8" data-testid="plan-scheduler-section">
        <div className="max-w-3xl mx-auto">
          <div
            className="bg-white rounded-lg border border-[#E8E4DC] overflow-hidden shadow-sm"
            data-testid="plan-nylas-embed"
          >
            <NylasScheduler
              configurationId={NYLAS_CONFIGURATION_ID}
              clientId={NYLAS_CLIENT_ID}
              bookingConfirmationRedirectUrl={bookingConfirmationRedirectUrl}
              fallbackUrl={NYLAS_FALLBACK_URL}
              testid="plan-nylas"
              onBooking={handleBooking}
            />
          </div>
          <p className="mt-6 text-center text-xs text-[#999]">
            Prefer a different time?{' '}
            <a
              href={NYLAS_FALLBACK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline underline-offset-4 hover:text-[#1A1A1A]"
              data-testid="plan-nylas-fallback-link"
            >
              {c.get('hero.fallback_label', 'Open scheduler in a new tab')}
            </a>
          </p>
        </div>
      </section>
    </div>
  );
}
